// Get API URL from environment variable
// In browser/client code, we use the injected __API_URL__ from build
// Fallback to process.env for server-side rendering or development
const API_URL =
  (typeof window !== "undefined" && (window as any).__API_URL__) ||
  (typeof process !== "undefined" && process.env?.BUN_PUBLIC_API_URL) ||
  (typeof process !== "undefined" && process.env?.API_URL) ||
  "http://localhost:8001/";

// Ensure API_URL ends with a slash
const baseURL = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface ApiError {
  error: string;
  details?: string | string[];
}

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}

export class AuthorizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthorizationError";
  }
}

// Get stored token from localStorage
export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

// Set token in localStorage
export function setToken(token: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem("access_token", token);
}

// Remove token from localStorage
export function removeToken(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem("access_token");
  localStorage.removeItem("token_type");
}

// Login function
export async function login(
  credentials: LoginCredentials
): Promise<LoginResponse> {
  const response = await fetch(`${baseURL}login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(credentials),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || "Login failed");
  }

  return data as LoginResponse;
}

// API client for authenticated requests
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const url = endpoint.startsWith("http")
    ? endpoint
    : `${baseURL}${endpoint.replace(/^\//, "")}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    // Handle authentication errors
    if (response.status === 401) {
      throw new AuthenticationError((data as ApiError).error || "Invalid or expired token");
    }
    // Handle authorization errors
    if (response.status === 403) {
      throw new AuthorizationError((data as ApiError).error || "Admin access required");
    }
    throw new Error((data as ApiError).error || "Request failed");
  }

  return data as T;
}

// Artist types
export interface Artist {
  id: number;
  name: string;
  hidden: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface Pagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ArtistsResponse {
  artists: Artist[];
  pagination: Pagination;
}

export interface GetArtistsParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: "id" | "name" | "created_at" | "updated_at";
  order?: "asc" | "desc";
}

export interface CreateArtistData {
  name: string;
  hidden?: boolean;
}

export interface UpdateArtistData {
  name?: string;
  hidden?: boolean;
}

export interface BulkCreateArtistsData {
  names: string[];
  hidden?: boolean;
}

export interface BulkCreateArtistsResponse {
  created: number;
  skipped: number;
  created_names: string[];
  skipped_names: string[];
}

// Get artists with pagination, search, and sorting
export async function getArtists(params: GetArtistsParams = {}): Promise<ArtistsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page !== undefined) {
    queryParams.append("page", params.page.toString());
  }
  if (params.per_page !== undefined) {
    queryParams.append("per_page", params.per_page.toString());
  }
  if (params.search) {
    queryParams.append("search", params.search);
  }
  if (params.sort) {
    queryParams.append("sort", params.sort);
  }
  if (params.order) {
    queryParams.append("order", params.order);
  }

  const queryString = queryParams.toString();
  const endpoint = `admin/artists${queryString ? `?${queryString}` : ""}`;
  
  return apiRequest<ArtistsResponse>(endpoint);
}

// Get a single artist by ID
export async function getArtist(id: number): Promise<Artist> {
  return apiRequest<Artist>(`admin/artists/${id}`);
}

// Create a new artist
export async function createArtist(data: CreateArtistData): Promise<Artist> {
  return apiRequest<Artist>("admin/artists", {
    method: "POST",
    body: JSON.stringify({
      name: data.name,
      hidden: data.hidden ?? false,
    }),
  });
}

// Update an existing artist
export async function updateArtist(id: number, data: UpdateArtistData): Promise<Artist> {
  return apiRequest<Artist>(`admin/artists/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// Delete an artist
export async function deleteArtist(id: number): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`admin/artists/${id}`, {
    method: "DELETE",
  });
}

// Bulk create artists
export async function bulkCreateArtists(data: BulkCreateArtistsData): Promise<BulkCreateArtistsResponse> {
  return apiRequest<BulkCreateArtistsResponse>("admin/artists/bulk", {
    method: "POST",
    body: JSON.stringify({
      names: data.names,
      hidden: data.hidden ?? false,
    }),
  });
}

// User types
export interface CurrentUser {
  id: number;
  email: string;
  role: string;
  created_at: string;
}

// Get current authenticated user
export async function getCurrentUser(): Promise<CurrentUser> {
  return apiRequest<CurrentUser>("admin/me");
}
